import { toast } from "sonner";
import {
  useUpdateManufacturerAddress,
  type ManufacturerAddress,
} from "@/api/manufacturer-tracking";
import {
  InlineFieldGroup,
  type InlineGroupField,
} from "@/components/inline-field-group";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type AddressField = "address1" | "address2" | "city" | "state" | "zip_code";

const FIELDS: ReadonlyArray<InlineGroupField<AddressField>> = [
  { key: "address1", label: "Street" },
  { key: "address2", label: "Suite, unit, building" },
  { key: "city", label: "City" },
  { key: "state", label: "State", className: "col-span-3" },
  { key: "zip_code", label: "ZIP", className: "col-span-3" },
];

function errorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

/** Edit a manufacturer's address (USA-1105). */
export function AddressSheet({
  manufacturerUuid,
  manufacturerName,
  address,
  open,
  onOpenChange,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  address: Partial<Record<AddressField, string | null>>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
      >
        {open && (
          <AddressForm
            manufacturerUuid={manufacturerUuid}
            manufacturerName={manufacturerName}
            address={address}
            onDone={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function AddressForm({
  manufacturerUuid,
  manufacturerName,
  address,
  onDone,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  address: Partial<Record<AddressField, string | null>>;
  onDone: () => void;
}) {
  const update = useUpdateManufacturerAddress(manufacturerUuid);
  const values: Record<AddressField, string> = {
    address1: address.address1 ?? "",
    address2: address.address2 ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
    zip_code: address.zip_code ?? "",
  };

  const save = (draft: Record<AddressField, string>) => {
    const changed: ManufacturerAddress = {};
    for (const { key } of FIELDS) {
      const value = draft[key].trim() || null;
      if (value !== (address[key] ?? null)) changed[key] = value;
    }
    if (Object.keys(changed).length === 0) {
      onDone();
      return;
    }
    update.mutate(changed, {
      onSuccess: () => {
        toast.success("Address saved");
        onDone();
      },
      onError: (error) =>
        toast.error(errorDetail(error, "Could not save the address")),
    });
  };

  return (
    <>
      <SheetHeader className="border-b px-4 py-4 sm:px-6">
        <SheetTitle>Address</SheetTitle>
        <SheetDescription>{manufacturerName}</SheetDescription>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {/* The sheet opens straight into editing; Done or Cancel closes it (USA-1580). */}
        <InlineFieldGroup
          label="Address"
          fields={FIELDS}
          values={values}
          pending={update.isPending}
          editing
          onEditingChange={(editing) => {
            if (!editing) onDone();
          }}
          onSave={save}
        >
          {FIELDS.map(({ key }) => values[key])
            .filter(Boolean)
            .join(", ") || "No address on file"}
        </InlineFieldGroup>
      </div>
    </>
  );
}
